/**
 * Data state management utilities
 * This file provides a standardized way to handle loading, error, and success states
 * in data fetching operations throughout the application.
 */

/**
 * Defines a standard error structure with message and optional status code
 */
export interface DataError {
  message: string;
  statusCode?: number;
}

/**
 * Represents the possible states of data fetching
 */
export type DataState<T> = 
  | { status: 'loading' }
  | { status: 'error', error: DataError }
  | { status: 'loaded', data: T };

/**
 * Creates a loading state object
 */
export const createLoadingState = <T>(): DataState<T> => ({ 
  status: 'loading' 
});

/**
 * Creates an error state object with the provided error information
 * @param error The error object or message
 */
export const createErrorState = <T>(error: DataError | string): DataState<T> => ({ 
  status: 'error', 
  error: typeof error === 'string' ? { message: error } : error
});

/**
 * Creates a loaded state object with the provided data
 * @param data The successfully loaded data
 */
export const createLoadedState = <T>(data: T): DataState<T> => ({ 
  status: 'loaded', 
  data 
});

/**
 * Type guard to check if the state is in loading status
 * @param state The data state to check
 */
export const isLoading = <T>(state: DataState<T>): state is { status: 'loading' } => 
  state.status === 'loading';

/**
 * Type guard to check if the state is in error status
 * @param state The data state to check
 */
export const isError = <T>(state: DataState<T>): state is { status: 'error', error: DataError } => 
  state.status === 'error';

/**
 * Type guard to check if the state is in loaded status
 * @param state The data state to check
 */
export const isLoaded = <T>(state: DataState<T>): state is { status: 'loaded', data: T } => 
  state.status === 'loaded';

/**
 * Map a data state to a new value, similar to Array.map()
 * @param state The data state to map
 * @param fn The mapping function to apply to the data if in loaded state
 */
export const mapDataState = <T, R>(
  state: DataState<T>, 
  fn: (data: T) => R
): DataState<R> => {
  if (isLoaded(state)) {
    return createLoadedState(fn(state.data));
  }
  // For loading and error states, just change the type parameter
  return state as unknown as DataState<R>;
}; 